import { useState, useMemo } from 'react';
import styles from './ModifierSheet.module.css';

const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

/**
 * Hoja de selección de modificadores al agregar un producto al carrito.
 *
 * Un grupo con max_select 1 se comporta como radio; con más de 1, como
 * checkboxes topados en el máximo. No deja confirmar hasta cumplir los
 * mínimos de cada grupo, y dice cuál falta.
 */
export default function ModifierSheet({ product, groups, onConfirm, onClose }) {
  // { [groupId]: [optionId] }
  const [selected, setSelected] = useState({});

  const toggle = (group, option) => {
    setSelected((prev) => {
      const actuales = prev[group._id] ?? [];
      const yaEsta   = actuales.includes(option._id);

      if (group.max_select === 1) {
        // Radio: volver a tocar la opción elegida la deselecciona, pero solo
        // si el grupo es opcional — si es obligatorio no tiene sentido vaciarlo.
        if (yaEsta && group.min_select === 0) return { ...prev, [group._id]: [] };
        return { ...prev, [group._id]: [option._id] };
      }

      if (yaEsta) {
        return { ...prev, [group._id]: actuales.filter((id) => id !== option._id) };
      }
      if (actuales.length >= group.max_select) return prev;   // tope alcanzado
      return { ...prev, [group._id]: [...actuales, option._id] };
    });
  };

  // Grupos que aún no cumplen su mínimo
  const faltantes = useMemo(
    () => groups.filter((g) => (selected[g._id] ?? []).length < g.min_select),
    [groups, selected]
  );

  const elegidos = useMemo(() => {
    const out = [];
    for (const g of groups) {
      for (const optId of selected[g._id] ?? []) {
        const opt = g.options.find((o) => o._id === optId);
        if (opt) {
          out.push({
            group_id:    g._id,
            group_name:  g.name,
            option_name: opt.name,
            price_delta: opt.price_delta ?? 0,
          });
        }
      }
    }
    return out;
  }, [groups, selected]);

  const extra = elegidos.reduce((acc, m) => acc + m.price_delta, 0);
  const precio = product.price + extra;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>

        <div className={styles.header}>
          <div>
            <p className={styles.productName}>{product.name}</p>
            <p className={styles.basePrice}>{fmt(product.price)}</p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        <div className={styles.body}>
          {groups.map((group) => {
            const actuales = selected[group._id] ?? [];
            const opciones = group.options.filter((o) => o.active !== false);
            const tope     = group.max_select > 1 && actuales.length >= group.max_select;

            return (
              <div key={group._id} className={styles.group}>
                <div className={styles.groupHead}>
                  <p className={styles.groupName}>{group.prompt || group.name}</p>
                  <span className={`${styles.groupRule} ${group.min_select > 0 ? styles.groupRequired : ''}`}>
                    {group.min_select > 0
                      ? (group.max_select === 1 ? 'Obligatorio' : `Elige ${group.min_select}–${group.max_select}`)
                      : (group.max_select === 1 ? 'Opcional' : `Hasta ${group.max_select}`)}
                  </span>
                </div>

                <div className={styles.options}>
                  {opciones.map((opt) => {
                    const activa    = actuales.includes(opt._id);
                    const bloqueada = tope && !activa;
                    return (
                      <button
                        key={opt._id}
                        className={`${styles.option} ${activa ? styles.optionActive : ''}`}
                        onClick={() => toggle(group, opt)}
                        disabled={bloqueada}
                      >
                        <span className={`${styles.mark} ${group.max_select === 1 ? styles.markRadio : ''}`}>
                          {activa && '✓'}
                        </span>
                        <span className={styles.optionName}>{opt.name}</span>
                        {opt.price_delta !== 0 && (
                          <span className={styles.optionDelta}>
                            {opt.price_delta > 0 ? '+' : '−'}{fmt(Math.abs(opt.price_delta))}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>
          {faltantes.length > 0 && (
            <p className={styles.pending}>
              Falta elegir: {faltantes.map((g) => g.name).join(', ')}
            </p>
          )}
          <button
            className="btn btn--primary btn--full"
            disabled={faltantes.length > 0}
            onClick={() => onConfirm(elegidos)}
          >
            Agregar · {fmt(precio)}
          </button>
        </div>

      </div>
    </div>
  );
}
